import type { ComponentProps } from 'react'
import { core } from '@musinsa/pbo-core'
import { SkuUnmappedError } from '../queries/useModifyInbounds'

interface Props {
  error: SkuUnmappedError | null
  onClose: () => void
}

/** 안내 문구(사용자 확정 2026-09-10) — 두 줄. 서버 meta.message(1줄째와 같은 계약 문구)는 화면에 직접 싣지 않는다. */
export const SKU_UNMAPPED_NOTICE = 'SKU 정보를 찾을 수 없어 저장에 실패했어요.\n아래 바코드의 매핑 상태를 SCM-hub에서 확인하세요.'

/** 확인 단독 푸터(컨덕터 안내 모달 `okOnly` 규약) — 렌더 밖에 두어 매 렌더 새 컴포넌트가 되지 않게 한다. */
const okOnlyFooter: NonNullable<ComponentProps<typeof core.Modal>['footer']> = (_, { OkBtn }) => <OkBtn />

/**
 * SKU 미매핑 저장 차단 안내 — 1.0 SkuFailModal 파리티.
 *
 * BE 는 미매핑 행이 하나라도 있으면 저장을 전량 차단하고 HTTP 422 + meta.errorCode=ERROR_INBOUND_SKU_UNMAPPED
 * + data.failedBarcodes 로 응답한다(LOGISPRD-9000 FE 계약). 바코드가 없는 미매핑 행만 있으면
 * failedBarcodes 가 빈 배열로 온다 — 그때도 차단 사실은 알려야 한다.
 *
 * 푸터는 기본형 확인 하나(취소 없음, 닫기 = 확인 — 컨덕터 안내 모달 `okOnly` 규약. 버튼 유형은 사용자 결정으로 default). X·마스크는 그대로 닫힌다.
 */
export function SkuUnmappedModal({ error, onClose }: Readonly<Props>) {
  return (
    <core.Modal
      open={Boolean(error)}
      title="저장 실패"
      width={520}
      okText="확인"
      // 확인은 기본형(primary 아님) — 안내 모달의 단독 버튼은 강조하지 않는다(사용자 확정 2026-09-10).
      okButtonProps={{ type: 'default' }}
      onOk={onClose}
      onCancel={onClose}
      footer={okOnlyFooter}
    >
      <core.Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>{SKU_UNMAPPED_NOTICE}</core.Typography.Paragraph>
      {/* BE 는 행 단위로 모아 주므로 같은 바코드가 여러 번 올 수 있다(SkuGate — distinct 없음).
          그대로 그리면 React key 가 겹치고 목록에도 같은 값이 반복된다. */}
      {error?.failedBarcodes.length ? (
        <div
          style={{
            maxHeight: 240,
            overflowY: 'auto',
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: '8px 12px',
            background: '#fafafa',
          }}
        >
          {[...new Set(error.failedBarcodes)].map((barcode) => (
            <div key={barcode} style={{ fontSize: 13, lineHeight: '22px' }}>
              {barcode}
            </div>
          ))}
        </div>
      ) : null}
    </core.Modal>
  )
}

export default SkuUnmappedModal
